import {
  LitElement,
  html,
  css,
  classMap,
  ref,
  createRef,
  map,
  range,
  unsafeHTML,
  styles,
  paths,
  utils,
  t,
} from '@hh/global.js'
const { throttle } = utils

class HHImageCarousel extends LitElement {
  static properties = {
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
    speed: {
      reflect: true,
      type: Number,
      converter: {
        fromAttribute: (value) => {
          return Number(value * 1000)
        },
      },
    },
    playing: {
      reflect: true,
      type: Boolean,
    },
    userPause: {
      type: Boolean,
    },
    autoplay: {
      state: true,
    },
    toggle: {
      reflect: true,
      type: Boolean,
    },
    pagination: {
      reflect: true,
      type: Boolean,
    },
    counter: {
      reflect: true,
      type: Boolean,
    },
    arrows: {
      reflect: true,
      type: Boolean,
    },
    intersectionObserver: {
      reflect: true,
      type: Boolean,
      attribute: 'intersection-observer',
    },
    slideCount: {
      type: Number,
      attribute: 'slide-count',
    },
  }

  $slides = createRef()
  $next = createRef()
  $prev = createRef()
  $togglePlay = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 1
    this.playing = false
    this.userPause = false
    this.highlightCompleted = false

    this.touchstartX = 0
    this.touchendX = 0
    this.swipeThreshold = 50

    this.throttledUpdateUI = throttle(this.updateUI.bind(this), 300)
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this._total = this.items.length

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  firstUpdated() {
    this.$slides?.value?.addEventListener('scroll', this.throttledUpdateUI)

    if (
      this.intersectionObserver &&
      this.speed &&
      !this.reducedMotion.matches
    ) {
      this.setPlayPauseIntersectionObserver()
    } else if (this.speed && !this.reducedMotion.matches) {
      this.setAutoplay()
    } else {
      this.autoplay = false
      this.userPause = true
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('_current')) {
      this.setSlideVisibility()
    }
  }

  updateUI() {
    if (!this._total) return

    this._current =
      Math.round(this.$slides?.value?.scrollLeft / this.items[0].clientWidth) +
      1
    this._current === 1
      ? this.$prev?.value?.setAttribute('disabled', true)
      : this.$prev?.value?.removeAttribute('disabled')

    this._current === this._total
      ? this.$next?.value?.setAttribute('disabled', true)
      : this.$next?.value?.removeAttribute('disabled')
  }

  setSlideVisibility() {
    this.items.forEach((item, idx) => {
      const linkElements = item.querySelectorAll('hh-button, button, a')
      if (linkElements.length === 0) return

      const idxOffset = idx + 1
      if (idxOffset === this._current) {
        linkElements.forEach((button) => {
          if (button.tagName.toLowerCase() == 'a' || 'button') {
            button.removeAttribute('tabindex')
          }
          const linkElements = button.shadowRoot?.querySelectorAll('a, button')
          linkElements?.forEach((el) => el.removeAttribute('tabindex'))
        })
        item.setAttribute('aria-hidden', 'false')
        item.removeAttribute('tabindex')
      } else {
        linkElements.forEach((button) => {
          if (button.tagName.toLowerCase() == 'a' || 'button') {
            button.setAttribute('tabindex', '-1')
          }
          const linkElement = button.shadowRoot?.querySelectorAll('a, button')
          linkElement?.forEach((el) => el.setAttribute('tabindex', '-1'))
        })
        item.setAttribute('aria-hidden', 'true')
        item.setAttribute('tabindex', '-1')
      }
    })
  }

  // Autoplay methods
  setAutoplay() {
    if (!this.speed) return

    clearInterval(this.autoplay)
    this.autoplay = setInterval(this.rotateSlides.bind(this), this.speed)
    this.play()
  }

  stopAutoplay() {
    clearInterval(this.autoplay)
    this.pause()
  }

  toggleAutoplay() {
    if (this.playing) {
      this.userPause = true
      this.stopAutoplay()
    } else {
      this.userPause = false
      this.setAutoplay()
    }
  }

  play() {
    this.container?.setAttribute('aria-live', 'off')
    this.playing = true
  }

  pause() {
    this.container?.setAttribute('aria-live', 'polite')
    this.playing = false
  }

  rotateSlides() {
    const slideScrollPosition =
      this._current === this._total
        ? 0
        : this.$slides?.value?.scrollLeft + this.items[0].clientWidth
    this.$slides?.value?.scrollTo({
      left: slideScrollPosition,
    })

    if (this._current !== this._total) {
      const nextSlideImage = this.items[this._current].querySelector('img')
      this.eagerLoadImage(nextSlideImage)
    }
  }

  setPlayPauseIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) =>
        entry.isIntersecting ? this.onFocusOut() : this.onFocusIn()
      )
    })
    observer.observe(this.$slides?.value)
  }

  // Early load image for next slide
  eagerLoadImage(image) {
    if (!image) return
    image.setAttribute('loading', 'eager')
  }

  // Move to next or previous slide
  move(e) {
    e.preventDefault()
    this.userPause = true
    this.stopAutoplay()
    const slideScrollPosition =
      e.currentTarget.name === 'next'
        ? this.$slides?.value?.scrollLeft + this.items[0].clientWidth
        : this.$slides?.value?.scrollLeft - this.items[0].clientWidth
    this.$slides?.value?.scrollTo({
      left: slideScrollPosition,
    })
  }

  // Move to specific slide
  moveTo(slide) {
    const slideScrollPosition = this.items[slide].offsetLeft
    this.$slides?.value?.scrollTo({
      left: slideScrollPosition,
    })

    const nextSlideImage = this.items[slide].querySelector('img')
    this.eagerLoadImage(nextSlideImage)
  }

  // Event handlers
  onFocusIn() {
    if (this.userPause || this.reducedMotion.matches) return
    this.stopAutoplay()
  }

  onFocusOut() {
    if (this.userPause || this.reducedMotion.matches) return
    this.setAutoplay()
  }

  onPaginationClick(e, slideIndex) {
    e.preventDefault()
    this.userPause = true
    this.stopAutoplay()
    this.moveTo(slideIndex)
  }

  onTouchStart(e) {
    this.touchstartX = e.changedTouches[0].screenX
  }

  onTouchEnd(e) {
    this.touchendX = e.changedTouches[0].screenX
    this.checkSwipe()
  }

  checkSwipe() {
    if (
      this.touchendX < this.touchstartX - this.swipeThreshold ||
      this.touchendX > this.touchstartX + this.swipeThreshold
    ) {
      this.userPause = true
      this.stopAutoplay()
    }
  }

  render() {
    return html`<!-- @ts-ignore no-incompatible-type-binding -->
      <div
        class="slides"
        ${ref(this.$slides)}
        @touchstart="${this.onTouchStart}"
        @touchend="${this.onTouchEnd}"
        @mouseenter="${this.onFocusIn}"
        @mouseleave="${this.onFocusOut}"
        @focusin="${this.onFocusIn}"
        @focusout="${this.onFocusOut}"
      >
        <slot name="item"></slot>
      </div>
      <div class="controls">
        ${/* Pagination */ ''}
        ${(this.pagination &&
          html`
            <div
              class="${classMap({
                pagination: true,
                hidden: this.slideCount === 1,
              })}"
            >
              ${map(range(this.slideCount), (idx) => {
                return html`
                  <button
                    class="pagination-button"
                    aria-label="${t(
                      'sections.image_carousel.go_to_slide'
                    )} ${idx + 1}"
                    aria-current="${this._current === idx + 1}"
                    @click="${(e) => this.onPaginationClick(e, idx)}"
                  ></button>
                `
              })}
            </div>
          `) ||
        ''}
        ${/* Play/pause button */ ''}
        ${(this.toggle &&
          html`
            <button
              class="${classMap({
                toggle: true,
                hidden: !this.speed || this.slideCount === 1,
              })}"
              @click=${this.toggleAutoplay}
              aria-label=${this.playing
                ? t('sections.image_carousel.pause')
                : t('sections.image_carousel.play')}
            >
              <span
                class="${classMap({
                  hidden: this.playing,
                })}"
                >${unsafeHTML(paths.play)}</span
              >
              <span
                class="${classMap({
                  hidden: !this.playing,
                })}"
                >${unsafeHTML(paths.pause)}</span
              >
            </button>
          `) ||
        ''}
        ${/* Counter */ ''}
        ${(this.counter &&
          html`
            <div class="counter">
              <span class="counter--current">${this._current}</span>
              <span aria-hidden="true"> / </span>
              <span class="visually-hidden">${t('accessibility.of')}</span>
              <span class="counter--total"
                >${this._total ? this._total : ''}</span
              >
            </div>
          `) ||
        ''}
        ${/* Previous/next buttons */ ''}
        ${(this.arrows &&
          html`
            <div class="arrows">
              <button
                ${ref(this.$prev)}
                @click="${this.move}"
                class="arrow arrow--prev"
                name="previous"
                aria-label="${t('accessibility.previous_slide')}"
              >
                ${unsafeHTML(paths.caret)}
              </button>
              <button
                ${ref(this.$next)}
                @click="${this.move}"
                class="arrow arrow--next"
                name="next"
                aria-label="${t('accessibility.next_slide')}"
              >
                ${unsafeHTML(paths.caret)}
              </button>
            </div>
          `) ||
        ''}
      </div>`
  }

  static styles = [
    styles.visuallyHidden,
    styles.hidden,
    css`
      *,
      *::before,
      *::after {
        box-sizing: border-box;
      }

      :host {
        position: relative;
      }

      .slides {
        display: flex;
        flex-wrap: nowrap;
        align-items: var(--hh-align-items, center);
        position: relative;
        width: 100%;
        background-color: var(--hh-carousel-background, transparent);
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        scrollbar-width: none;
        -webkit-overflow-scrolling: touch;
      }

      .slides::-webkit-scrollbar {
        display: none;
      }

      .controls {
        position: absolute;
        padding: 1rem 3rem;
        bottom: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        column-gap: 1rem;
      }

      .pagination {
        display: flex;
        column-gap: 1rem;
      }

      .pagination-button {
        width: var(--hh-pagination-button-size, 1.2rem);
        height: var(--hh-pagination-button-size, 1.2rem);
        background-color: var(--hh-color-background);
        border-radius: 50%;
        border: none;
        cursor: pointer;
      }

      .toggle {
        width: var(--hh-toggle-button-size, 2.4rem);
        height: var(--hh-toggle-button-size, 2.4rem);
        padding: 0;
        color: var(--hh-color-background);
        background-color: transparent;
        border: none;
        cursor: pointer;
        flex-shrink: 0;
      }

      .toggle svg {
        width: 100%;
        height: 100%;
      }

      .counter {
        display: flex;
        column-gap: 0.5rem;
        color: var(--hh-color-background);
      }

      .arrows {
        display: flex;
        column-gap: 0.5rem;
      }

      .arrow {
        width: var(--hh-arrow-button-size, 2.4rem);
        height: var(--hh-arrow-button-size, 2.4rem);
        padding: 0;
        color: var(--hh-color-background);
        background-color: transparent;
        border: none;
        cursor: pointer;
      }

      .arrow--prev {
        transform: rotate(90deg);
      }

      .arrow--next {
        transform: rotate(-90deg);
      }
    `,
  ]
}

customElements.define('hh-image-carousel', HHImageCarousel)
